/**
 * Copyright 2019-2021 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import {
  getStoredYmmeActionSource,
  removeYmmeActionSource,
} from '@/features/ymme/utils/ymmeActionSourceHelper';
import { type InteractionLocation } from '@/types/analytics';
import { clickTrack } from './analytics/clickTrack';
import { ymmeActionSources } from './ymmeHelpers';
import root from 'window-or-global';
import { type HeaderData } from '@/features/header';
import { getStoredInteractionLocation } from '@/features/ymme/utils/ymmeLocationHelper';
import type { AcesFlags } from '@/features/ymme/utils/acesFlags';

type AnalyticsVehicleData = {
  userVehicleCount: string | undefined;
  vehicleTypeName?: string | undefined;
  year: string | undefined;
  make: string | undefined;
  model: string | undefined;
  subModel?: string | undefined;
  engine: string | undefined;
  id: string | undefined;
  interactionLocation: InteractionLocation;
  actionSource: string | undefined;
};

export const addVehicle = (vehicleData: AnalyticsVehicleData, acesFlags?: AcesFlags) => {
  const { pageName = '', pageType = '', siteSection = '' } = root.utag_data || {};
  const customLinkName = `${pageType}:${vehicleData.actionSource || ymmeActionSources.directURL}`;
  const vehicleInteractionLocation: InteractionLocation = vehicleData.interactionLocation ?? 'URL';
  const { acesEnabled, acesTransitionPhase, partsProFlag } = acesFlags || {};
  const additionalQuestions = acesTransitionPhase === false && partsProFlag === false;

  clickTrack({
    eventType: 'addVehicle',
    userVehicleCount: vehicleData.userVehicleCount,
    ...(acesEnabled
      ? {
          vehicleInformation: formatVehicleInformation(vehicleData),
          ...(additionalQuestions
            ? {
                // Submodel and Engine are optional fields
                Submodel: vehicleData.subModel ?? 'Null',
                Engine: vehicleData.engine ?? 'Null',
              }
            : {}),
        }
      : {
          vehicleYear: vehicleData.year,
          vehicleMake: vehicleData.make,
          vehicleModel: vehicleData.model,
          vehicleEngine: vehicleData.engine,
          vehicleId: vehicleData.id,
        }),
    vehicle_interaction_location: vehicleInteractionLocation,
    pageName,
    pageType,
    siteSection,
    customLinkName: customLinkName,
  });
  removeYmmeActionSource();
};

/**
 * Formats vehicleInformation for ACES analytics with any unanswered optional questions
 * interpolated as empty strings
 * @return 'Vehicle Type|Year|Make|Model|SubMode|Engine'
 */
const formatVehicleInformation = (vehicleData: AnalyticsVehicleData) => {
  const { year = '', make = '', model = '', vehicleTypeName = '' } = vehicleData;
  return `${vehicleTypeName}|${year}|${make}|${model}`;
};

export const trackAddVehicle = ({
  isAddByURL,
  headerData,
  acesFlags,
}: {
  isAddByURL: boolean;
  headerData?: HeaderData;
  acesFlags?: AcesFlags;
}) => {
  const { vehicleCount, year, make, model, subModel, engine, vehicleTypeName, catalogVehicleId } =
    headerData?.vehicleMap || {};

  addVehicle(
    {
      userVehicleCount: vehicleCount,
      vehicleTypeName,
      year,
      make,
      model,
      subModel,
      engine,
      id: catalogVehicleId,
      interactionLocation: isAddByURL ? 'URL' : getStoredInteractionLocation(),
      actionSource: getStoredYmmeActionSource(),
    },
    acesFlags
  );
};
